import createBreakpoints from '@material-ui/core/styles/createBreakpoints'
import { createTheme } from '@material-ui/core/styles';
import { deDE } from '@material-ui/core/locale';
import { grey } from '@material-ui/core/colors';

/**
 * Color definitions
 */
const colorPrimary = '#d40e14';
const colorSecondary = '#d40e14';

/**
 * Some basics used in many
 */
const heading: { color: string, fontWeight: number, textTransform: 'uppercase' } = {
    color: colorPrimary,
    fontWeight: 700,
    textTransform: 'uppercase',
};

const applySpacing = (factor: number) => `${factor}rem`;
const breakpoints = createBreakpoints({})

/**
 * Create a theme
 */
export default createTheme({
    /**
     * Adjust the color palette
     */
    palette: {
        primary: {
            main: colorPrimary,
        },
        secondary: {
            main: colorSecondary,
        },
    },
    /**
     * Adjust the typography
     */
    typography: {
        fontFamily: [
            'ApothekeFontCalibLight',
            '-apple-system',
            'BlinkMacSystemFont',
            '"Segoe UI"',
            'Roboto',
            '"Helvetica Neue"',
            'Arial',
            'sans-serif',
            '"Apple Color Emoji"',
            '"Segoe UI Emoji"',
            '"Segoe UI Symbol"',
        ].join(','),
    },
    /**
     * Spacing function to be used in custom stylings
     */
    spacing: applySpacing,
    /**
     * Default props configuration
     */
    props: {
        // Force outlined input stye
        MuiTextField: {
            variant: 'outlined',
            margin: 'normal',
        },
    },
    /**
     * Style overrides
     */
    overrides: {
        /**
         * General layout styles
         */
        // Some basic styles
        MuiCssBaseline: {
            '@global': {
                // Custom toolbar primary that flexes accoross the toolbar
                '.toolbar-primary': {
                    flex: 1,
                },
            },
        },
        // Appbar, adjust gaps and flex flow
        MuiAppBar: {
            root: {
                paddingTop: '0',
                paddingBottom: '0',
                marginBottom: '1rem',

                '& .MuiToolbar-root': {
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                },
            },
        },
        /**
         * General container has less spacing
         */
        MuiContainer: {
            root: {
                [breakpoints.down('sm')]: {
                    paddingLeft: applySpacing(1),
                    paddingRight: applySpacing(1),
                },
            },
        },
        /**
         * Typography
         */
        MuiTypography: {
            h1: {
                ...heading,
                fontSize: '2rem',
                marginBottom: applySpacing(2),
            },
            h2: { ...heading, fontSize: '1.7rem' },
            h3: { ...heading, fontSize: '1.4rem' },
            h4: { ...heading, fontSize: '1.25rem' },
            h5: { ...heading, fontSize: '1.1rem' },
            h6: { ...heading, fontSize: '1rem' },

            root: {
                overflowWrap: 'break-word',
                wordWrap: 'break-word',
                hyphens: 'auto',
            },

            subtitle1: {
                lineHeight: 1.4,
                fontSize: 18,
                '&.MuiTypography-gutterBottom': {
                    marginBottom: applySpacing(2),
                },
            },

            gutterBottom: {
                marginBottom: applySpacing(1),
            },
        },
        // Icon adjustment
        MuiSvgIcon: {
            root: {
                verticalAlign: 'middle',
            },
            colorPrimary: {
                color: '#45a200',
            },
        },

        /**
         * Dialogs
         */
        MuiDialogTitle: {
            root: {
                '& .MuiIconButton-root': {
                    position: 'absolute',
                    top: '.5rem',
                    right: '.5rem',
                },
            },
        },
        /**
         * Surface elements like papers and cards
         */
        // Custom rounding for paper
        MuiPaper: {
            rounded: {
                borderRadius: '2px',
            },
        },
        // Adjust padding on cards
        MuiCard: {
            root: {
                '& > .MuiList-root': {
                    '& > .MuiListItem-gutters': {
                        paddingLeft: applySpacing(3),
                        paddingRight: applySpacing(3),
                    },
                    '& > .MuiListItem-container': {
                        '& > .MuiListItem-gutters': {
                            paddingLeft: applySpacing(3),
                            paddingRight: applySpacing(3),
                        },
                        '& > .MuiListItemSecondaryAction-root': {
                            right: applySpacing(3),
                        },
                    },
                },
            },
        },
        MuiCardContent: {
            root: {
                padding: `${applySpacing(2)} ${applySpacing(3)}`,
                [breakpoints.down('sm')]: {
                    paddingLeft: applySpacing(1),
                    paddingRight: applySpacing(1),
                },
            },
        },
        MuiCardHeader: {
            root: {
                padding: `${applySpacing(2)} ${applySpacing(3)}`,
                [breakpoints.down('sm')]: {
                    paddingLeft: applySpacing(1),
                    paddingRight: applySpacing(1),
                },
                '& + .MuiCardContent-root': {
                    paddingTop: 0,
                },
            },
        },

        /**
         * Tables
         */
        // Table toolbar spacing
        MuiToolbar: {
            root: {
                paddingTop: '1.5rem',
                paddingBottom: '1.5rem',
                paddingLeft: '1.5rem',
                paddingRight: '1.5rem',
                [breakpoints.down('sm')]: {
                    paddingLeft: applySpacing(1),
                    paddingRight: applySpacing(1),
                    paddingTop: applySpacing(0.5),
                    paddingBottom: applySpacing(0.5),
                },
            },
        },
        // Table row and cell spacing
        MuiTableRow: {
            root: {
                '& .MuiTableCell-root:first-child': {
                    paddingLeft: '1.5rem',
                },
                '& .MuiTableCell-root:last-child': {
                    paddingRight: '1.5rem',
                },
            },
        },
        // Table cell, colors and borders
        MuiTableCell: {
            root: {
                borderBottomWidth: 2,
            },
            body: {
                color: grey[900],
            },
            head: {
                color: grey[600],
                textTransform: 'uppercase',
            },
        },
        // Pagination coloring and sizing
        MuiTablePagination: {
            actions: {
                '& .MuiSvgIcon-root': {
                    fontSize: '3rem',
                },
                '& .MuiIconButton-colorInherit': {
                    color: colorPrimary,
                },
                '& .Mui-disabled': {
                    color: grey[400],
                },
            },
        },

        /**
         * Form elements
         */
        // Button styles, custom spacing and text
        MuiButton: {
            label: {
                textTransform: 'uppercase',
                fontWeight: 700,
            },
            contained: {
                paddingTop: applySpacing(.75),
                paddingBottom: applySpacing(.75),

                '&.Mui-disabled': {
                    backgroundColor: grey[400],
                    color: grey[100],
                },
            },
        },

        MuiFormGroup: {
            root: {
                marginBottom: '2rem',
            },
        },

        MuiCheckbox: {
            root: {
                alignSelf: 'flex-start',
                '& + .MuiFormControlLabel-label': {
                    textAlign: 'left',
                    paddingTop: 9,
                    paddingBottom: 9,
                },
            },
        },

        // Stepper control
        MuiStepper: {
            root: {
                backgroundColor: 'transparent',
                boxShadow: 'none',
                marginBottom: applySpacing(3),
            },
        },
        MuiStep: {
            completed: {
                '& .MuiStepIcon-root.MuiStepIcon-completed': {
                    color: 'green',
                },
                '& .MuiStepLabel-root > .MuiStepLabel-labelContainer': {
                    '@media(max-width: 959.95px)': {
                        display: 'none',
                    },
                },
            },
            root: {
                '& .MuiStepLabel-root.Mui-disabled > .MuiStepLabel-labelContainer': {
                    '@media(max-width: 959.95px)': {
                        display: 'none',
                    },
                },
            },
        },
        MuiStepIcon: {
            root: {
                width: '1.5em',
                height: '1.5em',
                color: grey[400],
            },
            text: {
                fontWeight: 700,
            },
        },
        MuiStepLabel: {
            label: {
                color: grey[400],
                fontSize: 16,
                marginBottom: 0,
            },
        },

        // Input disabled
        MuiOutlinedInput: {
            root: {
                '&.Mui-disabled': {
                    backgroundColor: grey[400],
                },
            },
        },

        MuiFormControl: {
            root: {
                '& + .MuiButton-root': {
                    marginTop: applySpacing(1),
                },
            },
            marginNormal: {
                marginBottom: applySpacing(1),
            },
        },
    },
}, deDE);
